import { useQuery } from '@apollo/client/react';
import React from 'react';
import { useParams, useSearchParams } from 'react-router';
import styled from 'styled-components';
import { MemberDocument } from '../../../graphql/types';
import { useGoBack } from '../../../hooks/useGoBack';
import { TrackingEventData } from '../../../hooks/useTracking';
import { BORDER_GRAY, WHITE } from '../../../theme';
import {
  EventCategories,
  EventNames,
  EventProfileCardNames,
} from '../../../utils/trackingEvents';
import { LinkButton } from '../Button/LinkButton';
import { ConfirmationModal } from '../ConfirmationModal';
import Text, { TextKind } from '../Text';

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;

  padding: 16px 24px;
  border-bottom: 1px solid ${BORDER_GRAY};
  background: ${WHITE};

  position: sticky;
  top: 0;
  z-index: 10;
`;

interface FeatureHeaderProps {
  name: 'training plan' | 'gameplan';
  trackingData?: TrackingEventData;
  state: 'loading' | 'create' | 'edit' | 'view';
  hasUnsavedChanges?: boolean;
}

export const FeatureHeader: React.FC<FeatureHeaderProps> = ({
  name,
  trackingData,
  state,
  hasUnsavedChanges,
}) => {
  const { memberId } = useParams();
  const { goBack } = useGoBack();
  const [searchParams] = useSearchParams();
  const navigationDepth =
    parseInt(searchParams.get('navigationDepth') || '1', 10) || 1;
  const { data: memberData } = useQuery(MemberDocument, {
    variables: { id: memberId! },
    skip: !memberId,
  });

  const [confirmationModalIsOpen, setConfirmationModalIsOpen] =
    React.useState(false);

  const handleExitNavigation = () => {
    goBack({
      fallbackPath: `/app/members/${memberId}`,
      popCount: navigationDepth,
    });
  };

  const onExitClick = () => {
    if (hasUnsavedChanges) {
      setConfirmationModalIsOpen(true);
      return;
    }
    handleExitNavigation();
  };

  const handleConfirmExit = () => {
    handleExitNavigation();
    setConfirmationModalIsOpen(false);
  };

  let title = '';
  if (state === 'create') {
    title = `New ${name} for ${memberData?.user?.firstName}`;
  } else if (state === 'edit') {
    title = `Editing ${name}`;
  } else if (state === 'view') {
    title = `Viewing ${name}`;
  }

  return (
    <>
      <StyledHeader>
        <Text kind={TextKind.TitleXs}>{title}</Text>
        <LinkButton
          onClick={onExitClick}
          trackingData={{
            eventName: EventNames.BackButtonClick,
            event_label: 'Exit',
            member_id: memberId,
            ...trackingData,
          }}
        >
          <Text kind={TextKind.ButtonM}>Exit</Text>
        </LinkButton>
      </StyledHeader>
      <ConfirmationModal
        isOpen={confirmationModalIsOpen}
        onConfirm={handleConfirmExit}
        confirmLabel="Yes, Cancel"
        cancelLabel="Nevermind"
        description="You have unsaved changes."
        title="Are you sure you want to cancel?"
        onClose={() => setConfirmationModalIsOpen(false)}
        trackingData={{
          ...trackingData,
          event_category:
            trackingData?.event_category ?? EventCategories.TrainingPlans,
          member_id: memberId,
          card_name: EventProfileCardNames.CancelConfirmModal,
        }}
      />
    </>
  );
};
